// AP/AR Approval Decision (docs/workflow.md step 6, issue 3 2026-08-11).
// Modeled on ImportControlScreen.tsx: a single-status, click-into-each queue
// with no bulk actions, because AP/AR now carries the review work Import
// Control used to do for every invoice — Import Control narrowed to settling
// an ambiguous company alone (owner ruling 4), so most invoices skip it
// entirely and arrive here still carrying whatever flags Matching raised.
//
// AP/AR is MANDATORY, not present-if-staffed like the three tiers below it: an
// invoice never routes around it, so a company with nobody appointed `ap-ar`
// freezes here — invisibly to this screen (owner ruling, 2026-08-14: a
// reviewer should either see an invoice and be able to act on it, or not see
// it at all — no visible-but-frozen third state). The Pipeline dialog's
// warning icon on this node is now the ONLY place that freeze is surfaced;
// see the comment on `filtered` below before touching either half of that.
//
// Company-scoped, exactly like Department/Director/Oversight — but unlike
// those three, which show every row and disable the ones the caller cannot
// decide, this screen's queue is FILTERED to `canDecide` up front (2026-08-14,
// see `filtered` below), so it only ever shows what the caller's `ap-ar` seat
// covers. Two outcomes, no deny (same as Import Control): `approved` releases
// the invoice — the company's approvers gain access — and `archived not an
// invoice` matches a document that turns out not to be a payable at all.
//
// AP/AR mounts the SAME shared editor Import Control has always used
// (`EditableInvoiceForm`, `editStep={DecisionStep.ApAr}`) — the full surface,
// unlocked: flags resolvable via Mark resolved, amounts editable, every line's
// expense type editable. The column rule is enforced INSIDE that editor, not
// by a narrowing prop here: it never renders an Account picker and never a
// "Who is it for?" column — the cost center is Department Approval's business
// or later, and the composed account belongs to Export Control and Manual
// Entry. The read-only view (no `editStep`) with the disabled Approve/Not An
// Invoice pair and `NOT_AUTHORIZED_REASON` below should not normally be
// reachable now that the queue is filtered — it remains as the modal's own
// fallback rather than a path a caller here is expected to hit.
//
// No per-row actions (owner item 5, 2026-08-13): the queue used to carry
// icon-only Approve/Archive buttons beside Open, but a reviewer must open the
// invoice — see its flags, amounts and the editable form — before deciding
// anything, so the row buttons offered a click that either 409'd or hid what
// it was approving. The only way to act here now is Open, then the modal's
// own editable action bar (decidable) or its disabled pair (not decidable).
import { useMemo, useState } from 'react'
import { Body1, Button, Toast, ToastTitle, makeStyles, tokens, useToastController } from '@fluentui/react-components'
import { CheckmarkRegular, DismissRegular } from '@fluentui/react-icons'
import { ScreenTitle } from '../components/ScreenTitle'
import type { Invoice } from '../lib/types'
import { DecisionOutcome, DecisionStep, InvoiceStatus, TOASTER_ID } from '../lib/constants'
import { createDecision } from '../lib/data'
import { errorMessage } from '../api/errors'
import { canDecide, canPreviewPdf, filterDecidable, type Session } from '../lib/identity'
import { useQueue } from '../lib/hooks'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { DecisionDialog } from '../components/DecisionDialog'
import { applyFilters, type InvoiceFilter } from '../lib/filters'
import { useDecisionButtonStyles } from '../lib/theme'
import { Loading, EmptyQueue, ErrorBanner } from '../components/States'

const useStyles = makeStyles({
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  sub: { color: tokens.colorNeutralForeground3 },
  modalActionButtons: { display: 'flex', gap: '8px' },
})

interface Props {
  session: Session
  refreshNonce?: number
  /** Page-wide filter (header Filter button; also drives the timeline badges). */
  filter: InvoiceFilter
  /** Called when acted rows leave the queue, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

const NOT_AUTHORIZED_REASON =
  "Only an AP/AR holder assigned to this invoice's own company can decide on it at this step. You can view it, but your decision would be refused."

export function ApArApprovalScreen({ session, refreshNonce, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const btn = useDecisionButtonStyles()
  const user = session.user
  const { invoices, loading, error, reload } = useQueue([InvoiceStatus.PendingApAr], refreshNonce)
  // Filtered to invoices the signed-in user can actually decide (owner
  // ruling, 2026-08-14, rejecting the earlier "no AP/AR holder" banner): "If
  // I have access to the invoice, then I should be able to see and act on
  // it. If I do not have access to the invoice, then it would just never
  // appear here." This covers both reasons an invoice's company might not be
  // this user's to decide — someone else holds `ap-ar` for it, or nobody
  // does — with one rule, so there is no visible-but-frozen third state.
  //
  // A company with NOBODY appointed `ap-ar` still freezes every invoice
  // there for everyone (AP/AR is mandatory, never skipped) — this filter
  // just stops surfacing that fact here. The Pipeline dialog's warning icon
  // on this node is now the ONLY place that freeze is discovered; it is
  // load-bearing for that reason and must not be removed or weakened as
  // part of any future AP/AR cleanup.
  //
  // Kept as two steps rather than one combined filter so the empty state
  // below can tell "the page filter narrowed it to nothing" apart from
  // "none of these are yours to decide" instead of blaming the wrong one.
  // `filterDecidable` (lib/identity.ts) is this screen's own rule, factored
  // out so every other decision screen follows the same one function.
  const pageFiltered = useMemo(() => applyFilters(invoices, filter), [invoices, filter])
  const filtered = useMemo(
    () => filterDecidable(session, DecisionStep.ApAr, pageFiltered),
    [pageFiltered, session],
  )

  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  // One source for one truth: the modal's editable-vs-read-only branch reads
  // this rather than computing `canDecide` on the open invoice a second time.
  // Filtering `filtered` above means every invoice reachable through this
  // screen's Open button is already decidable, so this should always be
  // true in practice — kept as the modal's own check rather than assumed,
  // since the client's `canDecide` hint is not the server's authority check.
  const openDecidable = !!openInvoice && canDecide(session, DecisionStep.ApAr, openInvoice)
  const [archiving, setArchiving] = useState<Invoice | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const { dispatchToast } = useToastController(TOASTER_ID)

  function settle() {
    setOpenInvoice(null)
    setArchiving(null)
    void reload()
    onQueuesChanged?.()
  }

  async function approve(invoice: Invoice) {
    setSubmitting(true)
    setActionError(null)
    try {
      await createDecision({
        invoice_id: invoice.id,
        step: DecisionStep.ApAr,
        decision: DecisionOutcome.Approved,
        row_version: invoice.row_version,
      })
      settle()
      dispatchToast(
        <Toast>
          <ToastTitle>Invoice approved and released to its company's approvers</ToastTitle>
        </Toast>,
        { intent: 'success' },
      )
    } catch (e) {
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  async function archiveNotAnInvoice(invoice: Invoice, reason: string) {
    setSubmitting(true)
    setActionError(null)
    try {
      await createDecision({
        invoice_id: invoice.id,
        step: DecisionStep.ApAr,
        decision: DecisionOutcome.ArchivedNotAnInvoice,
        reason,
        row_version: invoice.row_version,
      })
      settle()
      dispatchToast(
        <Toast>
          <ToastTitle>Archived — Not an Invoice</ToastTitle>
        </Toast>,
        { intent: 'success' },
      )
    } catch (e) {
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <div className={s.header}>
        <ScreenTitle>AP/AR Approval</ScreenTitle>
        <Body1 className={s.sub}>For each flagged invoice, resolve all outstanding issues to release for approval.</Body1>
      </div>

      <ErrorBanner message={error || actionError} />

      {loading ? (
        <Loading label="Loading held invoices…" />
      ) : filtered.length === 0 ? (
        <EmptyQueue
          text={
            pageFiltered.length > 0
              ? 'Nothing here is assigned to you right now.'
              : invoices.length > 0
                ? 'No invoices match the current filters.'
                : undefined
          }
        />
      ) : (
        <InvoiceQueue invoices={filtered} showFlags onOpen={setOpenInvoice} />
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf={canPreviewPdf(session)}
        user={user}
        editStep={openDecidable ? DecisionStep.ApAr : undefined}
        onChanged={() => void reload({ silent: true })}
        onClose={() => setOpenInvoice(null)}
        onReleased={() => {
          settle()
          dispatchToast(
            <Toast>
              <ToastTitle>Invoice approved and released to its company's approvers</ToastTitle>
            </Toast>,
            { intent: 'success' },
          )
        }}
        onArchived={() => {
          settle()
          dispatchToast(
            <Toast>
              <ToastTitle>Archived — Not an Invoice</ToastTitle>
            </Toast>,
            { intent: 'success' },
          )
        }}
        actions={
          // Rendered by the modal on its READ-ONLY branch only — a decidable
          // reviewer gets the editable form's own Save & Release / Not An
          // Invoice bar instead (`editStep` above), the same swap Import
          // Control has always had. The queue filter above means every
          // invoice reachable here is decidable, so this disabled pair
          // should not normally show; it stays as the modal's own fallback
          // rather than assuming the queue filter and the server's authority
          // check can never disagree.
          openInvoice && (
            <div className={s.modalActionButtons}>
              <Button
                className={btn.approve}
                icon={<CheckmarkRegular />}
                disabled={submitting || !openDecidable}
                title={openDecidable ? undefined : NOT_AUTHORIZED_REASON}
                onClick={() => approve(openInvoice)}
              >
                Approve
              </Button>
              <Button
                className={btn.deny}
                icon={<DismissRegular />}
                disabled={submitting || !openDecidable}
                title={openDecidable ? undefined : NOT_AUTHORIZED_REASON}
                onClick={() => setArchiving(openInvoice)}
              >
                Not An Invoice
              </Button>
            </div>
          )
        }
      />

      <DecisionDialog
        open={!!archiving}
        // The heading is the button text that opens this dialog (owner item
        // 5, second sentence, 2026-08-12) — "Not An Invoice" in the modal.
        title="Not An Invoice"
        body="This document will be archived as not an invoice. This comment is recorded on the record and is visible in the decision history."
        requireReason
        reasonLabel="Reason"
        confirmLabel="Archive"
        destructive
        submitting={submitting}
        onCancel={() => setArchiving(null)}
        onConfirm={(reason) => archiving && archiveNotAnInvoice(archiving, reason)}
      />
    </div>
  )
}
